/**
 * Entfernen-Bereich (Phase B, 22.07.2026): entfernt fuer eine Filtermenge
 * (Kategorie-Aeste + optional Hersteller) wahlweise den Kaufland-Kanal (102.01)
 * und/oder die Kaufland.de-Kategorie (Property 107) — zwei unabhaengige Schalter.
 *
 * Sicherheitsmechanik wie beim Schreiben: Vorschau-Pflicht, Testlauf mit N,
 * Massenlauf-Bestaetigung ab TESTRUN_THRESHOLD betroffenen Varianten.
 * Der Kategorie-Baum spiegelt den ManualTab (Kandidat fuer spaetere Extraktion
 * in eine gemeinsame Komponente — bewusst NICHT im selben PR refactored).
 */
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useJobStatus } from '@libero/core/hooks/useJobStatus';
import { api } from '@libero/core/services/api';
import {
  AlertCircle, AlertTriangle, ChevronDown, ChevronRight, Folder, Info, Loader2, Trash2,
} from 'lucide-react';

interface PlentyCategory {
  id: number;
  parentCategoryId: number | null;
  level: number;
  name: string;
  isActiveStandard?: boolean;
}

interface HerstellerOption {
  producerId: number;
  producerName: string;
}

interface RemovePreview {
  total: number;
  mitKanal: number;
  /** Basis-Kanal "Kaufland" (Referrer 102.00 / marketId 102). */
  mitKanalBasis: number;
  mitKategorie: number;
  katalogStand: string | null;
}

interface KauflandVariant {
  variationId: number;
  itemId: number;
  variantennr: string;
  artikelname: string;
  isMain: boolean;
  isActive: boolean;
  kauflandDE: boolean;
  kauflandBasis: boolean;
  kauflandKategorieId: string | null;
  kauflandKategorieName: string;
}

interface RemoveFieldResult {
  entfernt: number;
  bereitsEntfernt: number;
  fehler: number;
  fehlerDetails: string[];
}

interface RemoveRunResult {
  total: number;
  testModus: boolean;
  markt: RemoveFieldResult;
  marktBasis: RemoveFieldResult;
  property: RemoveFieldResult;
}

/** Ab dieser Zahl betroffener Varianten verlangt der Bestaetigungsdialog die Massenlauf-Checkbox. */
const TESTRUN_THRESHOLD = 20;

/** Seitengroesse der Artikel-Tabelle. */
const PAGE_SIZE = 100;

export default function RemoveTab() {
  // ---- Kategorien + Baum ----------------------------------------------------
  const [categories, setCategories] = useState<PlentyCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<number>>(new Set());
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [categorySearch, setCategorySearch] = useState('');

  // ---- Hersteller + Schalter ------------------------------------------------
  const [herstellerOptions, setHerstellerOptions] = useState<HerstellerOption[]>([]);
  const [selectedProducerIds, setSelectedProducerIds] = useState<Set<number>>(new Set());
  const [removeMarket, setRemoveMarket] = useState(false);
  const [removeMarketBasis, setRemoveMarketBasis] = useState(false);
  const [removeProperty, setRemoveProperty] = useState(false);

  // ---- Vorschau + Lauf --------------------------------------------------------
  const [preview, setPreview] = useState<RemovePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Artikel-Tabelle (seitenweise, wie manueller Tab)
  const [pageRows, setPageRows] = useState<KauflandVariant[]>([]);
  const [page, setPage] = useState(1);
  const [pageLoading, setPageLoading] = useState(false);
  const [testCount, setTestCount] = useState(5);
  const [showConfirm, setShowConfirm] = useState<null | { testLauf: boolean }>(null);
  const [massConfirmed, setMassConfirmed] = useState(false);

  const {
    status: jobStatus, isRunning, progress,
    result: jobResultRaw, error: jobError, startJob, reset: resetJob,
  } = useJobStatus(null);
  const jobResult = jobResultRaw as RemoveRunResult | null;

  // ---- Kategorien laden -------------------------------------------------------
  useEffect(() => {
    (async () => {
      setCategoriesLoading(true);
      try {
        const r = await api.get<{ success: boolean; data: PlentyCategory[] }>(
          '/programmes/kaufland-kategorisierung/categories',
        );
        if (r.data.success) setCategories(r.data.data);
      } catch (err: any) {
        setCategoriesError(err.response?.data?.error?.message || 'Fehler beim Laden der Kategorien');
      } finally {
        setCategoriesLoading(false);
      }
    })();
  }, []);

  // ---- Hersteller-Optionen bei Kategorie-Auswahl nachladen (debounced) ----------
  // UX-Fix 22.07.: vorher erschien der Hersteller-Filter erst nach "Vorschau laden".

  useEffect(() => {
    if (selectedCategoryIds.size === 0) {
      setHerstellerOptions([]);
      setSelectedProducerIds(new Set());
      return;
    }
    let abort = false;
    const timer = setTimeout(async () => {
      try {
        const r = await api.post<{ success: boolean; data: HerstellerOption[] }>(
          '/programmes/kaufland-kategorisierung/remove/hersteller',
          { categoryIds: Array.from(selectedCategoryIds) },
        );
        if (!abort && r.data.success) {
          setHerstellerOptions(r.data.data);
          // Gewaehlte Hersteller behalten, sofern sie in der neuen Auswahl noch vorkommen
          setSelectedProducerIds(prev => {
            const valid = new Set(r.data.data.map(h => h.producerId));
            return new Set(Array.from(prev).filter(id => valid.has(id)));
          });
        }
      } catch {
        if (!abort) setHerstellerOptions([]); // silent — Bereich bleibt dann ausgeblendet
      }
    }, 400);
    return () => { abort = true; clearTimeout(timer); };
  }, [selectedCategoryIds]);

  // ---- Baum-Logik (Spiegel von ManualTab) --------------------------------------
  const childrenOf = useMemo(() => {
    const m = new Map<number, PlentyCategory[]>();
    for (const c of categories) {
      const p = c.parentCategoryId ?? 0;
      if (!m.has(p)) m.set(p, []);
      m.get(p)!.push(c);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.name.localeCompare(b.name, 'de'));
    return m;
  }, [categories]);

  const rootCategories = useMemo(() => {
    const allIds = new Set(categories.map(c => c.id));
    return categories
      .filter(c => c.parentCategoryId === null || !allIds.has(c.parentCategoryId))
      .sort((a, b) => a.name.localeCompare(b.name, 'de'));
  }, [categories]);

  const searchHits = useMemo(() => {
    const q = categorySearch.trim().toLowerCase();
    if (!q) return null;
    return categories.filter(c => c.name.toLowerCase().includes(q) || String(c.id).includes(q));
  }, [categories, categorySearch]);

  const getAllDescendantIds = useCallback((rootId: number): number[] => {
    const out: number[] = [];
    const stack = [rootId];
    while (stack.length > 0) {
      const id = stack.pop()!;
      const kids = childrenOf.get(id) ?? [];
      for (const k of kids) { out.push(k.id); stack.push(k.id); }
    }
    return out;
  }, [childrenOf]);

  function toggleCategoryWithCascade(id: number) {
    const descendants = getAllDescendantIds(id);
    setSelectedCategoryIds(prev => {
      const next = new Set(prev);
      const targets = [id, ...descendants];
      const allCurrentlyOn = targets.every(t => next.has(t));
      if (allCurrentlyOn) for (const t of targets) next.delete(t);
      else for (const t of targets) next.add(t);
      return next;
    });
    setPreview(null); // Auswahl geaendert -> alte Zahlen sind ungueltig
  }

  function toggleExpand(id: number) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function nodeSelectionState(nodeId: number): 'all' | 'some' | 'none' {
    const ids = [nodeId, ...getAllDescendantIds(nodeId)];
    let on = 0;
    for (const i of ids) if (selectedCategoryIds.has(i)) on++;
    if (on === 0) return 'none';
    if (on === ids.length) return 'all';
    return 'some';
  }

  const renderNode = useCallback((cat: PlentyCategory, depth: number): React.ReactNode => {
    const children = childrenOf.get(cat.id) ?? [];
    const hasChildren = children.length > 0;
    const isExpanded = expanded.has(cat.id);
    const state = nodeSelectionState(cat.id);
    const inaktiv = cat.isActiveStandard === false;
    return (
      <div key={cat.id}>
        <div
          className="flex items-center gap-1 px-2 py-1.5 hover:bg-gray-50"
          style={{ paddingLeft: `${8 + depth * 16}px` }}
          title={inaktiv ? 'In Plenty nicht aktiv — Mandant „Standard" ist bei dieser Kategorie nicht angehakt' : undefined}
        >
          <button
            type="button"
            onClick={() => toggleExpand(cat.id)}
            className="p-0.5 text-gray-500 hover:text-[#1A1A1A] flex-shrink-0"
            aria-label={isExpanded ? 'Einklappen' : 'Aufklappen'}
          >
            {!hasChildren
              ? <span className="inline-block w-4" />
              : isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          <Folder size={13} className={`flex-shrink-0 ${inaktiv ? 'text-gray-300' : 'text-gray-400'}`} />
          <input
            type="checkbox"
            checked={state === 'all'}
            ref={el => { if (el) el.indeterminate = state === 'some'; }}
            onChange={() => toggleCategoryWithCascade(cat.id)}
            className="ml-1 flex-shrink-0"
          />
          <span
            onClick={() => toggleCategoryWithCascade(cat.id)}
            className={`text-sm cursor-pointer select-none truncate ${depth === 0 ? 'font-medium' : ''} ${inaktiv ? 'text-gray-400' : ''}`}
          >
            {cat.name}{' '}
            <span className={`text-xs font-normal ${inaktiv ? 'text-gray-300' : 'text-gray-400'}`}>
              (ID {cat.id}{hasChildren ? `, ${children.length} Sub` : ''})
            </span>
            {inaktiv && <span className="ml-1 text-xs font-normal text-gray-400 italic">inaktiv</span>}
          </span>
        </div>
        {isExpanded && hasChildren && children.map(child => renderNode(child, depth + 1))}
      </div>
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childrenOf, expanded, selectedCategoryIds]);

  // ---- Vorschau + Artikel-Tabelle -----------------------------------------------
  const fetchRemovePage = useCallback(async (p: number) => {
    setPageLoading(true);
    try {
      const r = await api.post<{ success: boolean; data: { rows: KauflandVariant[]; page: number } }>(
        '/programmes/kaufland-kategorisierung/remove/variants',
        {
          categoryIds: Array.from(selectedCategoryIds),
          producerIds: Array.from(selectedProducerIds),
          page: p,
          pageSize: PAGE_SIZE,
        },
      );
      if (r.data.success) {
        setPageRows(r.data.data.rows);
        setPage(r.data.data.page);
      }
    } catch {
      setPageRows([]); // Tabelle ist Zusatz-Ansicht — Fehler nicht blockierend
    } finally {
      setPageLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategoryIds, selectedProducerIds]);

  async function handleLoadPreview() {
    if (selectedCategoryIds.size === 0) {
      setPreviewError('Mindestens eine Kategorie auswaehlen');
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    setPageRows([]);
    resetJob();
    const categoryIds = Array.from(selectedCategoryIds);
    const producerIds = Array.from(selectedProducerIds);
    try {
      const p = await api.post<{ success: boolean; data: RemovePreview }>(
        '/programmes/kaufland-kategorisierung/remove/preview',
        { categoryIds, producerIds },
      );
      if (p.data.success) {
        setPreview(p.data.data);
        await fetchRemovePage(1);
      }
    } catch (err: any) {
      setPreviewError(err.response?.data?.error?.message || 'Fehler beim Laden der Vorschau');
    } finally {
      setPreviewLoading(false);
    }
  }

  function toggleProducer(id: number) {
    setSelectedProducerIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setPreview(null); // Filter geaendert -> Vorschau neu laden
  }

  // ---- Lauf starten --------------------------------------------------------------
  const betroffen = preview
    ? Math.max(
        removeMarket ? preview.mitKanal : 0,
        removeMarketBasis ? preview.mitKanalBasis : 0,
        removeProperty ? preview.mitKategorie : 0,
      )
    : 0;
  // Write-Kosten: 1 Write pro Variante und Feld (kein Batch-DELETE bekannt).
  const estWrites = preview
    ? (removeMarket ? preview.mitKanal : 0)
      + (removeMarketBasis ? preview.mitKanalBasis : 0)
      + (removeProperty ? preview.mitKategorie : 0)
    : 0;
  const estHours = estWrites > 75 ? Math.ceil(estWrites / 75) : 0;

  async function handleStart(testLauf: boolean) {
    setShowConfirm(null);
    setMassConfirmed(false);
    await startJob('/programmes/kaufland-kategorisierung/remove', {
      categoryIds: Array.from(selectedCategoryIds),
      producerIds: Array.from(selectedProducerIds),
      removeMarket,
      removeMarketBasis,
      removeProperty,
      ...(testLauf ? { testCount } : {}),
    });
  }

  const startDisabled = !preview || isRunning
    || (!removeMarket && !removeMarketBasis && !removeProperty) || betroffen === 0;

  // ---- Render ----------------------------------------------------------------------
  return (
    <div className="p-6 max-w-screen-xl mx-auto">
      <h1 className="text-2xl font-bold text-[#1A1A1A] mb-2">Kaufland-Zuordnungen entfernen</h1>

      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex gap-3 items-start">
        <AlertTriangle className="text-red-600 mt-0.5 flex-shrink-0" size={20} />
        <div className="text-sm text-[#1A1A1A]">
          <p className="font-semibold mb-1">Achtung: Dieser Bereich ENTFERNT Kaufland-Zuordnungen</p>
          <ol className="list-decimal list-inside space-y-1">
            <li>Kategorie-Ast(e) waehlen, optional nach Hersteller einschraenken, dann „Vorschau laden".</li>
            <li>Schalter setzen: Verkaufskanal (Artikel wird von Kaufland delistet) und/oder Kaufland.de-Kategorie — beide unabhaengig.</li>
            <li><strong>Immer zuerst Testlauf</strong> mit wenigen Varianten, Ergebnis in Plenty pruefen. Massenlaeufe kosten 1 Write pro Variante und Feld (Plenty-Limit ~75/h) und laufen entsprechend lange im Hintergrund.</li>
          </ol>
        </div>
      </div>

      {/* Kategorie-Baum */}
      <div className="bg-white border border-[#DAD9D9] rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold mb-3">
          Kategorien{selectedCategoryIds.size > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({selectedCategoryIds.size} ausgewaehlt)
            </span>
          )}
        </h2>
        {categoriesLoading && (
          <div className="text-sm text-gray-500 flex items-center gap-2 py-2">
            <Loader2 className="animate-spin" size={14} /> Lade Kategorien...
          </div>
        )}
        {categoriesError && (
          <div className="text-sm text-red-600 flex items-center gap-2 py-2">
            <AlertCircle size={14} /> {categoriesError}
          </div>
        )}
        {!categoriesLoading && !categoriesError && (
          <>
            <input
              type="text"
              value={categorySearch}
              onChange={e => setCategorySearch(e.target.value)}
              placeholder="Kategorien durchsuchen (Name oder ID) — leerlassen fuer Tree-Ansicht"
              className="w-full px-3 py-2 border border-[#DAD9D9] rounded text-sm mb-2"
            />
            <div className="max-h-96 overflow-y-auto border border-[#DAD9D9] rounded">
              {searchHits ? (
                searchHits.length === 0
                  ? <div className="text-sm text-gray-500 p-3">Keine Treffer.</div>
                  : searchHits.map(cat => {
                      const inaktiv = cat.isActiveStandard === false;
                      return (
                        <label key={cat.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-sm">
                          <input
                            type="checkbox"
                            checked={selectedCategoryIds.has(cat.id)}
                            onChange={() => toggleCategoryWithCascade(cat.id)}
                          />
                          <span className={inaktiv ? 'text-gray-400' : ''}>
                            {cat.name} <span className={`text-xs ${inaktiv ? 'text-gray-300' : 'text-gray-400'}`}>(ID {cat.id})</span>
                            {inaktiv && <span className="ml-1 text-xs text-gray-400 italic">inaktiv</span>}
                          </span>
                        </label>
                      );
                    })
              ) : (
                rootCategories.length === 0
                  ? <div className="text-sm text-gray-500 p-3">Keine Kategorien geladen.</div>
                  : rootCategories.map(root => renderNode(root, 0))
              )}
            </div>
          </>
        )}
      </div>

      {/* Hersteller-Filter */}
      {herstellerOptions.length > 0 && (
        <div className="bg-white border border-[#DAD9D9] rounded-lg p-4 mb-6">
          <h2 className="text-lg font-semibold mb-1">Hersteller-Filter</h2>
          <p className="text-xs text-gray-500 mb-3">
            Optional: nur Varianten dieser Hersteller entfernen (keine Auswahl = alle Hersteller).
            Nach Aenderung „Vorschau laden" erneut klicken.
          </p>
          <div className="flex flex-wrap gap-2">
            {herstellerOptions.map(h => (
              <label
                key={h.producerId}
                className={`flex items-center gap-1.5 px-2.5 py-1 border rounded text-sm cursor-pointer select-none ${
                  selectedProducerIds.has(h.producerId)
                    ? 'border-[#1A1A1A] bg-[#E9FF57]/40 font-medium'
                    : 'border-[#DAD9D9] hover:bg-gray-50'
                }`}
              >
                <input
                  type="checkbox"
                  className="hidden"
                  checked={selectedProducerIds.has(h.producerId)}
                  onChange={() => toggleProducer(h.producerId)}
                />
                {h.producerName}
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Vorschau + Schalter + Start */}
      <div className="bg-white border border-[#DAD9D9] rounded-lg p-4 mb-6">
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <button
            onClick={handleLoadPreview}
            disabled={previewLoading || selectedCategoryIds.size === 0 || isRunning}
            className="px-4 py-2 bg-[#E9FF57] text-[#1A1A1A] rounded font-medium hover:bg-[#d4ea4d] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {previewLoading && <Loader2 className="animate-spin" size={15} />}
            Vorschau laden
          </button>
          {preview?.katalogStand && (
            <span className="text-xs text-gray-500">
              Datenstand: {new Date(preview.katalogStand).toLocaleString('de-DE')} (naechtlicher Sync)
            </span>
          )}
        </div>

        {previewError && (
          <div className="text-sm text-red-600 flex items-center gap-2 mb-3">
            <AlertCircle size={14} /> {previewError}
          </div>
        )}

        {preview && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 max-w-3xl">
              <div className="border border-[#DAD9D9] rounded p-3 text-center">
                <div className="text-xl font-bold">{preview.total.toLocaleString('de-DE')}</div>
                <div className="text-xs text-gray-500">Varianten im Filter</div>
              </div>
              <div className="border border-[#DAD9D9] rounded p-3 text-center">
                <div className="text-xl font-bold">{preview.mitKanal.toLocaleString('de-DE')}</div>
                <div className="text-xs text-gray-500">mit Kanal Kaufland DE (102.01)</div>
              </div>
              <div className="border border-[#DAD9D9] rounded p-3 text-center">
                <div className="text-xl font-bold">{preview.mitKanalBasis.toLocaleString('de-DE')}</div>
                <div className="text-xs text-gray-500">mit Kanal Kaufland (102.00)</div>
              </div>
              <div className="border border-[#DAD9D9] rounded p-3 text-center">
                <div className="text-xl font-bold">{preview.mitKategorie.toLocaleString('de-DE')}</div>
                <div className="text-xs text-gray-500">mit Kaufland-Kategorie</div>
              </div>
            </div>

            <div className="flex flex-col gap-2 mb-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={removeMarket} onChange={e => setRemoveMarket(e.target.checked)} />
                <span>
                  <strong>Verkaufskanal Kaufland DE (102.01) entfernen</strong>{' '}
                  <span className="text-gray-500">({preview.mitKanal.toLocaleString('de-DE')} betroffen)</span>
                </span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={removeMarketBasis} onChange={e => setRemoveMarketBasis(e.target.checked)} />
                <span>
                  <strong>Verkaufskanal Kaufland (Basis 102.00) entfernen</strong>{' '}
                  <span className="text-gray-500">({preview.mitKanalBasis.toLocaleString('de-DE')} betroffen — der Basis-Kanal, ueber den Artikel auf Kaufland gelistet bleiben)</span>
                </span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={removeProperty} onChange={e => setRemoveProperty(e.target.checked)} />
                <span>
                  <strong>Kaufland.de-Kategorie entfernen</strong>{' '}
                  <span className="text-gray-500">({preview.mitKategorie.toLocaleString('de-DE')} betroffen — Property 107 wird geloescht)</span>
                </span>
              </label>
            </div>

            {estHours > 0 && (removeMarket || removeProperty) && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-4 max-w-xl">
                Geschaetzte Dauer: ~{estWrites.toLocaleString('de-DE')} Writes ≈ {estHours} Stunde(n)
                (Plenty-Limit ~75 Writes/h, kein Batch-DELETE). Der Job laeuft im Hintergrund weiter.
              </div>
            )}

            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-sm">
                <label className="text-gray-600">Testlauf mit</label>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={testCount}
                  onChange={e => setTestCount(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
                  className="w-16 px-2 py-1 border border-[#DAD9D9] rounded"
                />
                <button
                  onClick={() => setShowConfirm({ testLauf: true })}
                  disabled={startDisabled}
                  className="px-3 py-1.5 border border-red-300 text-red-700 rounded font-medium hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Testlauf starten
                </button>
              </div>
              <button
                onClick={() => setShowConfirm({ testLauf: false })}
                disabled={startDisabled}
                className="px-4 py-2 bg-red-600 text-white rounded font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Trash2 size={15} />
                {isRunning ? 'Laeuft...' : `Entfernen (${betroffen.toLocaleString('de-DE')} Varianten)`}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Artikel-Tabelle (seitenweise) */}
      {preview && (
        <div className="bg-white border border-[#DAD9D9] rounded-lg mb-6 overflow-hidden">
          <div className="px-4 py-3 border-b border-[#DAD9D9] flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-lg font-semibold">
              Artikel im Filter
              <span className="ml-2 text-sm font-normal text-gray-500">({preview.total.toLocaleString('de-DE')} Varianten)</span>
            </h2>
            {pageLoading && <Loader2 className="animate-spin text-gray-400" size={16} />}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600 bg-gray-50">
                  <th className="px-3 py-2 font-medium">VariantenNr</th>
                  <th className="px-3 py-2 font-medium">Artikelname</th>
                  <th className="px-3 py-2 font-medium">Typ</th>
                  <th className="px-3 py-2 font-medium">Aktiv</th>
                  <th className="px-3 py-2 font-medium">Kanal DE (102.01)</th>
                  <th className="px-3 py-2 font-medium">Kanal Kaufland (102.00)</th>
                  <th className="px-3 py-2 font-medium">Aktuelle Kategorie</th>
                  <th className="px-3 py-2 font-medium">Wird entfernt</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 && !pageLoading && (
                  <tr><td colSpan={8} className="px-3 py-4 text-gray-500">Keine Varianten in dieser Auswahl.</td></tr>
                )}
                {pageRows.map(v => {
                  const parts: string[] = [];
                  if (removeMarket && v.kauflandDE) parts.push('Kanal DE');
                  if (removeMarketBasis && v.kauflandBasis) parts.push('Kanal Kaufland');
                  if (removeProperty && v.kauflandKategorieId !== null) parts.push('Kategorie');
                  const aktion = parts.join(' + ') || '—';
                  return (
                    <tr key={v.variationId} className="border-t border-[#DAD9D9]/60">
                      <td className="px-3 py-1.5 font-mono text-xs whitespace-nowrap">{v.variantennr}</td>
                      <td className="px-3 py-1.5">{v.artikelname}</td>
                      <td className="px-3 py-1.5">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${v.isMain ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                          {v.isMain ? 'Haupt' : 'Sub'}
                        </span>
                      </td>
                      <td className={`px-3 py-1.5 ${v.isActive ? 'text-green-700' : 'text-red-600'}`}>{v.isActive ? 'Ja' : 'Nein'}</td>
                      <td className={`px-3 py-1.5 ${v.kauflandDE ? 'text-green-700' : 'text-red-600'}`}>{v.kauflandDE ? 'Ja' : 'Nein'}</td>
                      <td className={`px-3 py-1.5 ${v.kauflandBasis ? 'text-green-700' : 'text-red-600'}`}>{v.kauflandBasis ? 'Ja' : 'Nein'}</td>
                      <td className={`px-3 py-1.5 ${v.kauflandKategorieId === null ? 'text-gray-400 italic' : ''}`}>
                        {v.kauflandKategorieId === null ? 'nicht gesetzt' : v.kauflandKategorieName}
                      </td>
                      <td className={`px-3 py-1.5 ${aktion === '—' ? 'text-gray-400' : 'text-red-600 font-medium'}`}>{aktion}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2.5 border-t border-[#DAD9D9] flex items-center gap-3 text-sm">
            <button
              onClick={() => fetchRemovePage(page - 1)}
              disabled={page <= 1 || pageLoading}
              className="px-3 py-1 border border-[#DAD9D9] rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ‹ Zurueck
            </button>
            <span className="text-gray-600">
              Seite {page} / {Math.max(1, Math.ceil(preview.total / PAGE_SIZE))} · {preview.total.toLocaleString('de-DE')} Varianten
            </span>
            <button
              onClick={() => fetchRemovePage(page + 1)}
              disabled={page >= Math.ceil(preview.total / PAGE_SIZE) || pageLoading}
              className="px-3 py-1 border border-[#DAD9D9] rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Weiter ›
            </button>
            <span className="text-xs text-gray-500 ml-auto">
              Spalte „Wird entfernt“ folgt den beiden Schaltern oben.
            </span>
          </div>
        </div>
      )}

      {/* Bestaetigungs-Dialog */}
      {showConfirm && preview && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowConfirm(null)}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <AlertTriangle className="text-red-600" size={20} />
              {showConfirm.testLauf ? `Testlauf: ${testCount} Varianten` : 'Massenlauf bestaetigen'}
            </h3>
            <p className="text-sm mb-3">
              Entfernt wird:{' '}
              <strong>
                {[
                  removeMarket ? 'Verkaufskanal Kaufland DE (102.01)' : null,
                  removeMarketBasis ? 'Verkaufskanal Kaufland (Basis 102.00)' : null,
                  removeProperty ? 'Kaufland.de-Kategorie' : null,
                ].filter(Boolean).join(' + ')}
              </strong>
              {' '}— {showConfirm.testLauf
                ? `nur die ersten ${testCount} betroffenen Varianten.`
                : `${betroffen.toLocaleString('de-DE')} betroffene Varianten.`}
            </p>
            {!showConfirm.testLauf && betroffen >= TESTRUN_THRESHOLD && (
              <label className="flex items-start gap-2 text-sm mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={massConfirmed}
                  onChange={e => setMassConfirmed(e.target.checked)}
                />
                <span>
                  Ich habe einen Testlauf mit wenigen Varianten erfolgreich in Plenty geprueft und
                  bestaetige diesen Massenlauf ausdruecklich.
                </span>
              </label>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowConfirm(null); setMassConfirmed(false); }}
                className="px-4 py-2 border border-[#DAD9D9] rounded text-sm hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                onClick={() => handleStart(showConfirm.testLauf)}
                disabled={!showConfirm.testLauf && betroffen >= TESTRUN_THRESHOLD && !massConfirmed}
                className="px-4 py-2 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Jetzt entfernen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fortschritt */}
      {isRunning && (
        <div className="bg-white border border-[#DAD9D9] rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-sm mb-2">
            <Loader2 className="animate-spin" size={15} />
            Entfernen laeuft... {progress?.message ?? ''}
          </div>
          <div className="h-2 bg-[#DAD9D9] rounded">
            <div
              className="h-2 bg-red-500 rounded transition-all"
              style={{ width: `${progress?.percentage ?? 0}%` }}
            />
          </div>
        </div>
      )}

      {/* Fehler */}
      {jobError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-sm text-red-700 flex items-center gap-2">
          <AlertCircle size={16} /> {jobError}
        </div>
      )}

      {/* Ergebnis */}
      {jobStatus === 'completed' && jobResult && (
        <div className="bg-white border border-[#DAD9D9] rounded-lg p-4 mb-6">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            Ergebnis {jobResult.testModus && <span className="text-xs font-normal bg-[#E9FF57] px-2 py-0.5 rounded">Testlauf</span>}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl text-sm">
            <div className="border border-[#DAD9D9] rounded p-3">
              <div className="font-semibold mb-1">Kanal Kaufland DE (102.01)</div>
              <div>Entfernt: <strong>{jobResult.markt.entfernt.toLocaleString('de-DE')}</strong></div>
              <div className="text-gray-500">Bereits entfernt/nicht vorhanden: {jobResult.markt.bereitsEntfernt.toLocaleString('de-DE')}</div>
              <div className={jobResult.markt.fehler > 0 ? 'text-red-600' : 'text-gray-500'}>Fehler: {jobResult.markt.fehler}</div>
            </div>
            <div className="border border-[#DAD9D9] rounded p-3">
              <div className="font-semibold mb-1">Kanal Kaufland (Basis 102.00)</div>
              <div>Entfernt: <strong>{jobResult.marktBasis.entfernt.toLocaleString('de-DE')}</strong></div>
              <div className="text-gray-500">Bereits entfernt/nicht vorhanden: {jobResult.marktBasis.bereitsEntfernt.toLocaleString('de-DE')}</div>
              <div className={jobResult.marktBasis.fehler > 0 ? 'text-red-600' : 'text-gray-500'}>Fehler: {jobResult.marktBasis.fehler}</div>
            </div>
            <div className="border border-[#DAD9D9] rounded p-3">
              <div className="font-semibold mb-1">Kaufland.de-Kategorie (Property 107)</div>
              <div>Entfernt: <strong>{jobResult.property.entfernt.toLocaleString('de-DE')}</strong></div>
              <div className="text-gray-500">Bereits entfernt/nicht vorhanden: {jobResult.property.bereitsEntfernt.toLocaleString('de-DE')}</div>
              <div className={jobResult.property.fehler > 0 ? 'text-red-600' : 'text-gray-500'}>Fehler: {jobResult.property.fehler}</div>
            </div>
          </div>
          {(jobResult.markt.fehlerDetails.length > 0 || jobResult.marktBasis.fehlerDetails.length > 0 || jobResult.property.fehlerDetails.length > 0) && (
            <details className="mt-3 text-xs text-gray-600">
              <summary className="cursor-pointer">Fehler-Details</summary>
              <ul className="list-disc list-inside mt-1">
                {[...jobResult.markt.fehlerDetails, ...jobResult.marktBasis.fehlerDetails, ...jobResult.property.fehlerDetails].map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
            </details>
          )}
          {jobResult.testModus && (
            <p className="text-sm text-gray-600 mt-3 flex items-center gap-1.5">
              <Info size={14} />
              Testlauf fertig — Ergebnis jetzt in Plenty an den betroffenen Varianten pruefen, dann ggf. Massenlauf starten.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
